import React, { useMemo, useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    CardHeader,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MdOpenInNew, MdEdit } from "react-icons/md";
import { getActiveColorStyle } from "../../utils/colorUtils";
import blankSalesTaxPdf from "../../assets/pdfs/report/SalesTax_TX_blank.pdf";
import vitInstructionsPdf from "../../assets/pdfs/report/TX_VITstatement_instructions.pdf";
import declarationPdf from "../../assets/pdfs/report/50-244.pdf";
import classNames from "classnames";

const StandardTaxReports = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
    const { t } = useTranslation();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const [isLightTheme, setIsLightTheme] = useState(() =>
        typeof document !== "undefined" && document.body.classList.contains("white-content")
    );

    useEffect(() => {
        const syncTheme = () => {
            setIsLightTheme(document.body.classList.contains("white-content"));
        };
        syncTheme();
        const observer = new MutationObserver(syncTheme);
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    const pageTextColor = isLightTheme ? "#1d253b" : "rgba(255, 255, 255, 0.95)";
    const mutedTextColor = isLightTheme ? "#525f7f" : "rgba(255, 255, 255, 0.55)";
    const linkTextColor = activeColorStyle.borderColor;
    const reportCardBg = isLightTheme ? "#ffffff" : "#27293d";
    const reportCardBorder = isLightTheme
        ? "rgba(150, 150, 150, 0.2)"
        : "rgba(255, 255, 255, 0.14)";

    const titleTextStyle = {
        color: pageTextColor,
        fontWeight: 500,
        fontSize: "14px",
    };
    const linkTextStyle = {
        ...titleTextStyle,
        color: linkTextColor,
        cursor: "pointer",
        textDecoration: "none",
    };
    const subLinkTextStyle = {
        color: linkTextColor,
        fontSize: "13px",
        cursor: "pointer",
        textDecoration: "none",
    };

    const navigate = useNavigate();

    // APIs can be attached to these handlers later
    const handlePdfFill = (reportId) => {
        if (reportId === 'form1_seller_financed') {
            navigate('/standard-tax-reports/seller-financed-sales-tax-form');
        } else if (reportId === 'form2_vit') {
            navigate('/standard-tax-reports/vit-statement');
        } else if (reportId === 'form4_blank') {
            window.open('https://pdf.ac/-S4KO83e', '_blank');
        }
    };


    const ReportRow = ({ children }) => (
        <Row className="mb-3">
            {children}
        </Row>
    );

    const ReportCard = ({ title, showExternal = false, onExternal, onFill, hasSubAction = false, children }) => (
        <Card
            style={{
                height: "100%",
                border: `1px solid ${reportCardBorder}`,
                boxShadow: "none",
                backgroundColor: reportCardBg,
                color: pageTextColor,
            }}
            className="mb-0 shadow-none standard-tax-report-card"
        >
            <CardBody
                className="p-3 d-flex flex-column justify-content-center standard-tax-report-card-body"
                style={{ color: pageTextColor }}
            >
                {!hasSubAction ? (
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <span
                                onClick={showExternal ? onExternal : undefined}
                                style={showExternal || onFill ? linkTextStyle : titleTextStyle}
                                className={showExternal || onFill ? "hover-link" : undefined}
                            >
                                {title}
                            </span>
                            {showExternal && (
                                <MdOpenInNew
                                    size={16}
                                    style={{ marginLeft: "6px", cursor: "pointer", color: linkTextColor }}
                                    onClick={onExternal}
                                />
                            )}
                        </div>
                        <div>
                            {onFill && (
                                <Button
                                    size="sm"
                                    className="custom-fill-btn"
                                    style={{ padding: "6px 12px", fontSize: "12px", display: "flex", alignItems: "center" }}
                                    onClick={onFill}
                                >
                                    <MdEdit size={14} style={{ marginRight: "4px" }} /> Fill <span style={{ textTransform: "none", marginLeft: "4px", opacity: 0.9 }}>(.PDF)</span>
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div>
                        <div onClick={onFill} style={{ ...linkTextStyle, marginBottom: "8px" }} className="hover-link">
                            {title}
                        </div>
                        <div style={{ paddingLeft: "15px" }}>
                            {children}
                        </div>
                    </div>
                )}
            </CardBody>
        </Card>
    );

    const SubAction = ({ text, onExternal, onFill }) => (
        <div className="d-flex align-items-center">
            <span style={{ fontSize: "12px", marginRight: "8px", color: mutedTextColor }}>○</span>
            <span onClick={onExternal} style={subLinkTextStyle} className="hover-link">
                {text}
            </span>
            <MdOpenInNew
                size={14}
                style={{ marginLeft: "5px", marginRight: "12px", cursor: "pointer", color: linkTextColor }}
                onClick={onExternal}
            />
            <Button
                size="sm"
                className="custom-fill-btn"
                style={{ padding: "4px 10px", fontSize: "11px", display: "flex", alignItems: "center", margin: 0, height: "26px" }}
                onClick={onFill}
            >
                <MdEdit size={12} style={{ marginRight: "4px" }} /> Fill <span style={{ textTransform: "none", marginLeft: "4px", opacity: 0.9 }}>(.PDF)</span>
            </Button>
        </div>
    );

    return (
        <div
            className={classNames("content standard-tax-reports-page", {
                "standard-tax-reports-page--dark": !isLightTheme,
                "theme2-admin-content-compact": shellThemeVariant === "2",
                "theme2-standard-tax-reports-inset": shellThemeVariant === "2",
            })}
        >
            <style>
                {`
          .standard-tax-reports-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .standard-tax-reports-card-header h2,
          .standard-tax-reports-card-header h4 {
            font-size: 16px !important;
            margin-top: 10px;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .standard-tax-reports-page .card .card-body {
            padding: 15px;
            padding-top: 10px;
            padding-bottom: 0 !important;
          }
          .hover-link:hover {
            opacity: 0.8;
            text-decoration: underline;
          }
          .custom-fill-btn {
            background-color: #f2790f !important;
            border: none !important;
            color: #ffffff !important;
            transition: all 0.2s ease-in-out;
          }
          .custom-fill-btn:hover, .custom-fill-btn:focus, .custom-fill-btn:active {
            background-color: #d9690b !important;
            color: #ffffff !important;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1) !important;
          }
          body:not(.white-content) .standard-tax-reports-page .standard-tax-reports-card-header h2,
          body:not(.white-content) .standard-tax-reports-page .standard-tax-report-card,
          body:not(.white-content) .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body,
          body:not(.white-content) .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body div,
          body:not(.white-content) .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body span,
          body:not(.white-content) .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body strong {
            color: rgba(255, 255, 255, 0.95) !important;
          }
          body:not(.white-content) .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body .hover-link {
            color: var(--theme-tertiary-states) !important;
          }
          body:not(.white-content) .standard-tax-reports-page .standard-tax-report-card {
            background-color: #27293d !important;
            border-color: rgba(255, 255, 255, 0.14) !important;
          }
          body.white-content .standard-tax-reports-page .standard-tax-report-card {
            background-color: #ffffff !important;
            border-color: rgba(150, 150, 150, 0.2) !important;
          }
          body.white-content .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body,
          body.white-content .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body div,
          body.white-content .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body span,
          body.white-content .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body strong {
            color: #1d253b !important;
          }
          body.white-content .standard-tax-reports-page .standard-tax-report-card .standard-tax-report-card-body .hover-link {
            color: var(--theme-tertiary-states) !important;
          }
        `}
            </style>
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader className="standard-tax-reports-card-header">
                            <h2 className="mb-0" style={{ color: pageTextColor }}>
                                {t("routes.standardTaxReports")}
                            </h2>
                        </CardHeader>
                        <CardBody>

                            <ReportRow>
                                <Col md="6" className="mb-3 mb-md-0">
                                    <ReportCard
                                        title="TX - Motor Vehicle Seller-Financed Sales Tax Form"
                                        onFill={() => handlePdfFill('form1_seller_financed')}
                                        hasSubAction={true}
                                    >
                                        <SubAction
                                            text="TX Motor Vehicle Sales Tax Form (Blank)"
                                            onExternal={() => window.open(blankSalesTaxPdf, '_blank')}
                                            onFill={() => handlePdfFill('form1_seller_financed')}
                                        />
                                    </ReportCard>
                                </Col>
                                <Col md="6">
                                    <ReportCard
                                        title="TX - Dealers Inventory Tax Statement (VIT)"

                                        onFill={() => handlePdfFill('form2_vit')}
                                        hasSubAction={true}
                                    >
                                        <SubAction
                                            text="VIT Instructions"
                                            onExternal={() => window.open(vitInstructionsPdf, '_blank')}
                                            onFill={() => handlePdfFill('form2_vit')}
                                        />
                                    </ReportCard>
                                </Col>
                            </ReportRow>

                            <ReportRow>
                                <Col md="6" className="mb-3 mb-md-0">
                                    <ReportCard
                                        title={
                                            <span style={titleTextStyle}>
                                                TX - <strong style={{ fontWeight: 700, color: pageTextColor }}>Populated</strong> Dealers Motor Vehicle Inventory Declaration
                                            </span>
                                        }
                                    />
                                </Col>
                                <Col md="6">
                                    <ReportCard
                                        title={
                                            <span style={titleTextStyle}>
                                                TX - <strong style={{ fontWeight: 700, color: pageTextColor }}>Blank</strong> Dealers Motor Vehicle Inventory Declaration
                                            </span>
                                        }
                                        showExternal={true}
                                        onExternal={() => {
                                            const link = document.createElement('a');
                                            link.href = declarationPdf;
                                            link.download = '50-244.pdf';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                        onFill={() => handlePdfFill('form4_blank')}
                                    />
                                </Col>
                            </ReportRow>

                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default StandardTaxReports; 